import { AuthSplitLayout } from '@/components/auth/AuthSplitLayout';
import { PhoneAuthStepPanel } from '@/components/auth/PhoneAuthStepPanel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { refreshAuthProfileCache } from '@/hooks/queries/auth.queries';
import { useAuth } from '@/hooks/useAuth';
import { usePhoneAuthFlow } from '@/hooks/usePhoneAuthFlow';
import { usePhoneVerification } from '@/hooks/usePhoneVerification';
import { getApiErrorMessage } from '@/lib/api/error';
import { getPhoneNumberDigits } from '@/lib/phoneNumber';
import { AuthService } from '@/services/auth.service';
import type { AuthLoginResponseDto } from '@/types/dto/auth.dto';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

type PhoneAuthPageVariant = 'login' | 'register';

interface PhoneAuthPageProps {
  variant: PhoneAuthPageVariant;
}

const PAGE_COPY: Record<
  PhoneAuthPageVariant,
  {
    cardDescription: string;
    cardTitle: string;
    footerHref: string;
    footerLabel: string;
    footerPrefix: string;
    heroBenefits: string[];
    heroHighlight: string;
    heroTitle: string;
    mobileSubtitle: string;
  }
> = {
  login: {
    cardDescription: 'Receba um código para continuar, sem senha.',
    cardTitle: 'Entrar',
    footerHref: '/cadastro',
    footerLabel: 'Criar conta',
    footerPrefix: 'Primeira vez aqui?',
    heroBenefits: ['Encontre locais perto de você', 'Agende em poucos toques', 'Receba lembretes automáticos'],
    heroHighlight: 'perto de você',
    heroTitle: 'Encontre um horário',
    mobileSubtitle: 'Seus agendamentos em um só lugar',
  },
  register: {
    cardDescription: 'Receba um código para continuar, sem senha.',
    cardTitle: 'Criar conta',
    footerHref: '/entrar',
    footerLabel: 'Entrar',
    footerPrefix: 'Já tem conta?',
    heroBenefits: ['Salve seus locais favoritos', 'Acompanhe seus agendamentos', 'Gerencie tudo em um só lugar'],
    heroHighlight: 'com seu telefone',
    heroTitle: 'Crie sua conta',
    mobileSubtitle: 'Seus agendamentos em um só lugar',
  },
};

function getLocationRedirectPath(locationState: unknown, search: string) {
  const from =
    typeof locationState === 'object' && locationState !== null && 'from' in locationState
      ? locationState.from
      : new URLSearchParams(search).get('from');

  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//') && !from.includes('\\')) {
    try {
      const target = new URL(from, window.location.origin);
      if (
        target.origin === window.location.origin &&
        target.pathname !== '/entrar' &&
        target.pathname !== '/cadastro'
      ) {
        return `${target.pathname}${target.search}${target.hash}`;
      }
    } catch {
      return '/perfil';
    }
  }

  return '/perfil';
}

async function updateSignedUserName(
  queryClient: ReturnType<typeof useQueryClient>,
  user: AuthLoginResponseDto['user'],
  name: string,
  phoneNumber: string,
) {
  const updatedUser = await AuthService.updateProfile({
    fullName: name,
    phoneNumber: getPhoneNumberDigits(phoneNumber) || user.phoneNumber || undefined,
  });

  await refreshAuthProfileCache(queryClient, updatedUser ?? user);
}

export function PhoneAuthPage({ variant }: PhoneAuthPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { isAuthenticated, isLoading, setVerificationToken, signIn } = useAuth();
  const phoneAuthFlow = usePhoneAuthFlow({ storageKey: `phone-auth:${variant}` });
  const phoneVerification = usePhoneVerification({
    phoneNumber: phoneAuthFlow.phoneNumber,
    verificationCode: phoneAuthFlow.verificationCode,
  });
  const [authenticatedUser, setAuthenticatedUser] = useState<AuthLoginResponseDto['user'] | null>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [isCompletingProfile, setIsCompletingProfile] = useState(false);
  const redirectPath = getLocationRedirectPath(location.state, location.search);
  const isQueueLogin = new URL(redirectPath, window.location.origin).pathname.endsWith('/walk-in');
  const pageCopy = isQueueLogin
    ? {
        ...PAGE_COPY[variant],
        cardTitle: 'Identifique-se para entrar na fila',
        cardDescription:
          'Confirme seu telefone para garantir seu lugar com um profissional. Os serviços ficam para depois.',
        mobileSubtitle: 'Primeiro, confirme seu telefone',
        heroTitle: 'Seu lugar na fila',
        heroHighlight: 'começa aqui',
        heroBenefits: [
          'Escolha seu profissional',
          'Decida o serviço agora ou no local',
          'Acompanhe sua posição e receba avisos',
        ],
      }
    : PAGE_COPY[variant];

  const handleRequestCode = async (method: 'whatsapp' | 'sms') => {
    const hasRequestedCode = await phoneVerification.requestCode(method);

    if (!hasRequestedCode) {
      return;
    }

    phoneAuthFlow.setVerificationCode('');
    phoneAuthFlow.setAuthStep('code');
  };

  const handleValidateCode = async () => {
    const validationResponse = await phoneVerification.validateCode();

    if (!validationResponse) {
      return;
    }

    setVerificationToken(validationResponse.verificationToken);
    setIsSigningIn(true);

    try {
      const signInResponse = await signIn({
        verificationToken: validationResponse.verificationToken,
      });
      const mustCompleteProfile =
        !validationResponse.hasLinkedUser ||
        Boolean(validationResponse.requiresProfileCompletion) ||
        signInResponse.requiresProfileCompletion;

      setAuthenticatedUser(signInResponse.user);

      if (mustCompleteProfile) {
        phoneAuthFlow.setUserName(signInResponse.user.name?.trim() || phoneAuthFlow.userName);
        phoneAuthFlow.setAuthStep('name');
        return;
      }

      phoneAuthFlow.reset();
      navigate(redirectPath, { replace: true });
    } catch (error) {
      toast.error('Não foi possível entrar', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setIsSigningIn(false);
    }
  };

  const handleCompleteProfile = async () => {
    const trimmedUserName = phoneAuthFlow.userName.trim();

    if (!authenticatedUser || !trimmedUserName || isCompletingProfile) {
      return;
    }

    setIsCompletingProfile(true);

    try {
      await updateSignedUserName(queryClient, authenticatedUser, trimmedUserName, phoneAuthFlow.phoneNumber);
      phoneAuthFlow.reset();
      navigate(redirectPath, { replace: true });
    } catch (error) {
      toast.error('Não foi possível concluir seu cadastro', {
        description: getApiErrorMessage(error, 'Tente novamente em instantes.'),
      });
    } finally {
      setIsCompletingProfile(false);
    }
  };

  if (isLoading) return <div className="bg-background min-h-screen" />;

  if (isAuthenticated && !isSigningIn && !authenticatedUser) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <AuthSplitLayout
      heroTitle={pageCopy.heroTitle}
      heroHighlight={pageCopy.heroHighlight}
      heroBenefits={pageCopy.heroBenefits}
      mobileSubtitle={pageCopy.mobileSubtitle}
    >
      <Card className="border-0 shadow-xl lg:shadow-2xl">
        <CardHeader className="pb-2 text-center">
          <CardTitle className="text-2xl font-bold">{pageCopy.cardTitle}</CardTitle>
          <CardDescription className="text-base">{pageCopy.cardDescription}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 pt-6">
          <PhoneAuthStepPanel
            authStep={phoneAuthFlow.authStep}
            isRequestingCode={phoneVerification.isRequestingCode}
            isValidatingCode={phoneVerification.isValidatingCode || isSigningIn}
            nameDescription="Informe seu nome para concluir o cadastro."
            onAuthStepChange={phoneAuthFlow.setAuthStep}
            onPhoneNumberChange={phoneAuthFlow.setPhoneNumber}
            onRequestCode={handleRequestCode}
            onUserNameChange={phoneAuthFlow.setUserName}
            onValidateCode={() => void handleValidateCode()}
            onVerificationCodeChange={phoneAuthFlow.setVerificationCode}
            phoneNumber={phoneAuthFlow.phoneNumber}
            phoneRequestMethod={phoneVerification.requestedMethod}
            userName={phoneAuthFlow.userName}
            verificationCode={phoneAuthFlow.verificationCode}
          />

          {phoneAuthFlow.authStep === 'name' && (
            <Button
              type="button"
              className="h-12 w-full text-base font-semibold"
              disabled={phoneAuthFlow.userName.trim().length === 0 || isCompletingProfile}
              onClick={() => void handleCompleteProfile()}
            >
              {isCompletingProfile ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Continuando...
                </>
              ) : (
                'Continuar'
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      <p className="text-muted-foreground mt-6 text-center text-sm">
        {pageCopy.footerPrefix}{' '}
        <Link
          to={`${pageCopy.footerHref}${location.search}`}
          state={location.state}
          className="text-primary font-semibold hover:underline"
        >
          {pageCopy.footerLabel}
        </Link>
      </p>
    </AuthSplitLayout>
  );
}
